import type { TypeaheadTravelDates } from '@luxuryescapes/contract-search'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'

type CarouselCardType = 'hotel' | 'experience' | 'cruise'

export interface CarouselSearchDateState {
  initialDates?: TypeaheadTravelDates
  /** Viewport month for the mobile vertical date panel when nothing concrete is selected. */
  focusMonth?: Date
}

function getConcreteStopDates(itineraryData: TripItineraryData): {
  stopId: number | null
  checkIn?: string
  checkOut?: string
} {
  const stop = itineraryData.stops.find(candidate => candidate.startDate && candidate.endDate) ??
    itineraryData.stops.find(candidate => candidate.startDate) ??
    itineraryData.stops[0]

  return {
    stopId: stop?.id ?? null,
    checkIn: stop?.startDate ?? itineraryData.startDate,
    checkOut: stop?.endDate ?? itineraryData.endDate,
  }
}

/**
 * Prefers streamed carousel dates from the assistant. When those are missing,
 * seeds the chip/picker from the same trip search defaults used by listing drawers.
 * Also returns a mobile viewport focus month (indicative / trip hint) in one pass.
 */
export function resolveCarouselSearchDateState({
  searchDates,
  itineraryData,
  cardType,
}: {
  searchDates?: TypeaheadTravelDates
  itineraryData?: TripItineraryData
  cardType: CarouselCardType
}): CarouselSearchDateState {
  if (!itineraryData || cardType === 'cruise') {
    return { initialDates: searchDates, focusMonth: undefined }
  }

  const { stopId, checkIn, checkOut } = getConcreteStopDates(itineraryData)
  const surface = cardType === 'experience' ? 'experiences' : 'hotels'
  const resolved = resolveSearchDefaults({
    surface,
    stopId,
    recentSearches: itineraryData.recentSearches,
    indicativeSearchMonth: itineraryData.indicativeSearchMonth,
    concrete: { checkIn, checkOut },
  })

  const focusMonth = resolved.focusMonth

  if (searchDates) {
    return { initialDates: searchDates, focusMonth }
  }

  if (cardType === 'experience') {
    if (resolved.surface !== 'experiences') {
      return { initialDates: undefined, focusMonth }
    }
    if (resolved.dateRange.checkIn && resolved.dateRange.checkOut) {
      return {
        initialDates: {
          type: 'specific',
          checkIn: resolved.dateRange.checkIn,
          checkOut: resolved.dateRange.checkOut,
        },
        focusMonth,
      }
    }
    return { initialDates: undefined, focusMonth }
  }

  if (resolved.surface !== 'hotels') {
    return { initialDates: undefined, focusMonth }
  }
  if (resolved.dateSelection.type === 'specific') {
    return {
      initialDates: {
        type: 'specific',
        checkIn: resolved.dateSelection.checkIn,
        checkOut: resolved.dateSelection.checkOut,
      },
      focusMonth,
    }
  }

  return { initialDates: undefined, focusMonth }
}
